import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  Divider,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuth } from "../../firebase/authContext";
import {
  getCanonicalLastRead,
  getRecentChapters,
} from "../../bible/readingHistoryStorage";
import { formatContinueChapterTitle } from "../../bible/bookNames";
import { getReadChapters } from "../ChapterProgress/useChapterProgress";
import { syncCloudReadingRecords } from "../../firebase/readingSyncService";
import { useEdition } from "../../bible/edition";

const TOTAL_BIBLE_CHAPTERS = 1189; // 929 in OT + 260 in NT

const ReadingHistoryPage = () => {
  const navigate = useNavigate();
  const edition = useEdition();
  const { currentUser } = useAuth();

  const [lastRead, setLastRead] = useState(getCanonicalLastRead);
  const [recentChapters, setRecentChapters] = useState(getRecentChapters);
  const [readChaptersMap, setReadChaptersMap] = useState(getReadChapters);
  const [syncing, setSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);

  const loadData = () => {
    setLastRead(getCanonicalLastRead());
    setRecentChapters(getRecentChapters());
    setReadChaptersMap(getReadChapters());
  };

  useEffect(() => {
    loadData();
    const handleStorageChange = () => loadData();
    window.addEventListener("bible_last_read_changed", handleStorageChange);
    window.addEventListener("bible_read_status_changed", handleStorageChange);
    return () => {
      window.removeEventListener("bible_last_read_changed", handleStorageChange);
      window.removeEventListener("bible_read_status_changed", handleStorageChange);
    };
  }, []);

  const handleManualSync = async () => {
    if (!currentUser) return;
    setSyncing(true);
    await syncCloudReadingRecords(currentUser.uid);
    loadData();
    setSyncing(false);
    setSyncSuccess(true);
    setTimeout(() => setSyncSuccess(false), 3000);
  };

  const readCount = Object.keys(readChaptersMap).filter(
    (k) => Boolean(readChaptersMap[k])
  ).length;

  const progressPercent = Math.min(
    100,
    Math.round((readCount / TOTAL_BIBLE_CHAPTERS) * 1000) / 10
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <HistoryIcon color="primary" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              阅读记录与进度
            </Typography>
            <Typography variant="body2" color="text.secondary">
              追踪您的每日读经足迹，云端同步不丢失
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {currentUser ? (
            <Chip
              icon={<CloudDoneIcon fontSize="small" />}
              label="云端实时同步中"
              color="success"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          ) : (
            <Chip
              icon={<CloudOffIcon fontSize="small" />}
              label="本地离线模式 (登录后可跨设备同步)"
              color="warning"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}

          {currentUser && (
            <Tooltip title="立即同步云端记录">
              <IconButton
                size="small"
                onClick={handleManualSync}
                disabled={syncing}
                color="primary"
              >
                <RefreshIcon
                  fontSize="small"
                  sx={{
                    animation: syncing ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {syncSuccess && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          云端阅读记录已成功同步！
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              textAlign: "center",
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(0,0,0,0.015)",
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              已通读章节
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, my: 0.5, color: "primary.main" }}>
              {readCount}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              全圣经共 {TOTAL_BIBLE_CHAPTERS} 章
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              textAlign: "center",
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(0,0,0,0.015)",
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              圣经阅读达成率
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, my: 0.5, color: "success.main" }}>
              {progressPercent}%
            </Typography>
            <Box sx={{ width: "80%", mx: "auto", mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              textAlign: "center",
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.03)"
                  : "rgba(0,0,0,0.015)",
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              最近阅读章节
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                my: 0.5,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {lastRead ? formatContinueChapterTitle(lastRead, edition.id === "en") : "暂无记录"}
            </Typography>
            {lastRead && (
              <Button
                size="small"
                variant="text"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={() => navigate(`/chapter/${lastRead.chapterId}`)}
                sx={{ fontWeight: 700, p: 0 }}
              >
                继续阅读
              </Button>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Reading Timeline */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          mb: 4,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <AutoStoriesIcon color="primary" fontSize="small" />
          最近阅读足迹
        </Typography>

        {recentChapters && recentChapters.length > 0 ? (
          <List disablePadding>
            {recentChapters.map((item, idx) => {
              const isFinished = Boolean(readChaptersMap[item.chapterId]);
              const formattedDate = item.timestamp
                ? new Date(item.timestamp).toLocaleString("zh-CN", {
                    month: "numeric",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "";

              return (
                <React.Fragment key={item.chapterId + idx}>
                  <ListItem
                    sx={{
                      py: 1.5,
                      px: 1,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      "&:hover": {
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.02)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                      <ListItemIcon sx={{ minWidth: "auto" }}>
                        {isFinished ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <AutoStoriesIcon color="action" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {formatContinueChapterTitle(item, edition.id === "en")}
                        </Typography>
                        {item.title && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {item.title}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formattedDate}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/chapter/${item.chapterId}`)}
                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                      >
                        阅读
                      </Button>
                    </Box>
                  </ListItem>
                  {idx < recentChapters.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        ) : (
          <Box sx={{ textAlign: "center", py: 5, color: "text.secondary" }}>
            <AutoStoriesIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1.5 }} />
            <Typography variant="body1">暂无阅读记录</Typography>
            <Typography variant="caption" color="text.secondary">
              在圣经中打开任意一章，系统将自动为您记录阅读轨迹
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(edition.homePath)}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                前往选卷读经
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default ReadingHistoryPage;
